import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { ServiceCategory, ServiceStaff } from "@/types/appointments";
import { cn } from "@/lib/utils";
import { AppointmentsFilterSection } from "./AppointmentsFilterSection";
import { AppointmentsMiniCalendar } from "./AppointmentsMiniCalendar";
import { getStaffAccent, getStaffCategories } from "./utils";

interface AppointmentsFilterRailProps {
  currentDate: Date;
  onSelectDate: (date: Date) => void;
  onMonthChange: (date: Date) => void;
  serviceCategories: ServiceCategory[];
  serviceStaffList: ServiceStaff[];
  selectedServices: Set<string>;
  selectedStaff: Set<string>;
  expandedSections: Set<string>;
  onSectionToggle: (section: string) => void;
  onServiceToggle: (serviceId: string) => void;
  onServiceCategoryToggle: (category: string) => void;
  onStaffToggle: (staffId: string) => void;
  onStaffCategoryToggle: (category: string) => void;
  className?: string;
  showHeader?: boolean;
  onClose?: () => void;
  showStaffFilter?: boolean;
}

export const AppointmentsFilterRail = ({
  currentDate,
  onSelectDate,
  onMonthChange,
  serviceCategories,
  serviceStaffList,
  selectedServices,
  selectedStaff,
  expandedSections,
  onSectionToggle,
  onServiceToggle,
  onServiceCategoryToggle,
  onStaffToggle,
  onStaffCategoryToggle,
  className,
  showHeader = false,
  onClose,
  showStaffFilter = true,
}: AppointmentsFilterRailProps) => {
  const staffCategories = getStaffCategories(serviceStaffList);

  return (
    <Card
      className={cn(
        "flex h-full w-full shrink-0 flex-col overflow-hidden",
        className
      )}
    >
      {showHeader ? (
        <div className="flex items-center justify-between border-b border-border px-4 py-4 sm:px-5">
          <div>
            <h2 className="z-h2 text-foreground">Filter by</h2>
          </div>
          {onClose ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full border border-border"
              onClick={onClose}
              aria-label="Close filters"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      ) : null}

      <ScrollArea className="max-h-[40vh] flex-1 lg:h-full lg:max-h-none">
        <div className="flex flex-col gap-3.5 p-4 sm:p-5">
          <AppointmentsMiniCalendar
            currentDate={currentDate}
            onSelectDate={onSelectDate}
            onMonthChange={onMonthChange}
          />

          <AppointmentsFilterSection
            title="Services"
            isOpen={expandedSections.has("Services")}
            onToggle={() => onSectionToggle("Services")}
          >
            <div className="space-y-2">
              {serviceCategories.map((category) => {
                const allSelected = category.services.every((service) =>
                  selectedServices.has(service.id)
                );
                const someSelected = category.services.some((service) =>
                  selectedServices.has(service.id)
                );

                return (
                  <div
                    key={category.name}
                    className="rounded-lg border border-border bg-card p-2.5"
                  >
                    <div className="flex items-center gap-2.5">
                      <Checkbox
                        id={`category-services-${category.name}`}
                        checked={
                          allSelected
                            ? true
                            : someSelected
                              ? "indeterminate"
                              : false
                        }
                        onCheckedChange={() =>
                          onServiceCategoryToggle(category.name)
                        }
                        className="data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary/70 data-[state=indeterminate]:text-primary-foreground"
                      />
                      <label
                        htmlFor={`category-services-${category.name}`}
                        className="flex-1 cursor-pointer"
                      >
                        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                          <span className="z-body text-foreground">
                            {category.name}
                          </span>
                          <span className="z-caption text-muted-foreground">
                            ({category.services.length} service
                            {category.services.length === 1 ? "" : "s"})
                          </span>
                        </div>
                      </label>
                    </div>

                    <div className="mt-2 space-y-1 border-t border-border/70 pt-2">
                      {category.services.map((service) => (
                        <div
                          key={service.id}
                          className="flex items-start gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-accent"
                        >
                          <Checkbox
                            id={`service-${service.id}`}
                            checked={selectedServices.has(service.id)}
                            onCheckedChange={() => onServiceToggle(service.id)}
                            className="mt-0.5"
                          />
                          <label
                            htmlFor={`service-${service.id}`}
                            className="flex-1 cursor-pointer"
                          >
                            <div className="z-secondary text-foreground">
                              {service.name}
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </AppointmentsFilterSection>

          {showStaffFilter ? (
            <AppointmentsFilterSection
              title="Staff"
              isOpen={expandedSections.has("Staff")}
              onToggle={() => onSectionToggle("Staff")}
            >
              <div className="space-y-2">
                {staffCategories.map((category) => {
                  const categoryStaff = serviceStaffList.filter(
                    (staff) => staff.category === category
                  );
                  const allSelected = categoryStaff.every((staff) =>
                    selectedStaff.has(staff.doctor__id)
                  );
                  const someSelected = categoryStaff.some((staff) =>
                    selectedStaff.has(staff.doctor__id)
                  );

                  return (
                    <div
                      key={category}
                      className="rounded-lg border border-border bg-card p-2.5"
                    >
                      <div className="flex items-center gap-2.5">
                        <Checkbox
                          id={`category-staff-${category}`}
                          checked={
                            allSelected
                              ? true
                              : someSelected
                                ? "indeterminate"
                                : false
                          }
                          onCheckedChange={() =>
                            onStaffCategoryToggle(category)
                          }
                          className="data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary/70 data-[state=indeterminate]:text-primary-foreground"
                        />
                        <label
                          htmlFor={`category-staff-${category}`}
                          className="flex-1 cursor-pointer"
                        >
                          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                            <span className="z-body text-foreground">
                              {category}
                            </span>
                            <span className="z-caption text-muted-foreground">
                              ({categoryStaff.length} on team)
                            </span>
                          </div>
                        </label>
                      </div>

                      <div className="mt-2 space-y-1 border-t border-border/70 pt-2">
                        {categoryStaff.map((staff, index) => {
                          const accent = getStaffAccent(
                            staff.doctor__id,
                            index,
                            staff.category
                          );

                          return (
                            <div
                              key={staff.doctor__id}
                              className="flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-accent"
                            >
                              <Checkbox
                                id={`staff-${staff.doctor__id}`}
                                checked={selectedStaff.has(staff.doctor__id)}
                                onCheckedChange={() =>
                                  onStaffToggle(staff.doctor__id)
                                }
                              />
                              <label
                                htmlFor={`staff-${staff.doctor__id}`}
                                className="flex flex-1 cursor-pointer items-center gap-2"
                              >
                                <span className="z-secondary text-foreground">
                                  {staff.name}
                                </span>
                                <span
                                  aria-hidden="true"
                                  className={cn(
                                    "ml-auto h-3 w-6 shrink-0 rounded-sm border border-border/80",
                                    accent.dotClassName
                                  )}
                                />
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </AppointmentsFilterSection>
          ) : null}

          <AppointmentsFilterSection title="Location" disabled />
          <AppointmentsFilterSection title="Other" disabled />
        </div>
      </ScrollArea>
    </Card>
  );
};
